/**
 * HTTP Interceptor with Automatic Token Refresh
 * Story 4.9: Session Expiration Handling (AC#3, AC#4, AC#5)
 *
 * Factory function that creates an authenticated fetch wrapper:
 * - Adds Authorization header to requests
 * - Intercepts 401 responses and attempts token refresh
 * - Retries original request with new token on successful refresh
 * - Calls onSessionExpired callback if refresh fails
 * - Prevents infinite retry loops (single retry attempt)
 */

export interface HttpInterceptorConfig {
  /** Get current auth token from store */
  getToken: () => string | null;
  /** Refresh the auth token, returns new token or null on failure */
  refreshToken: () => Promise<string | null>;
  /** Called when session has expired and cannot be refreshed */
  onSessionExpired: () => void;
}

/**
 * Creates an authenticated fetch function with 401 retry logic
 *
 * @example
 * ```typescript
 * const authenticatedFetch = createAuthenticatedFetch({
 *   getToken: () => store.getState().auth.token,
 *   refreshToken: async () => {
 *     const result = await store.dispatch(refreshToken()).unwrap();
 *     return result;
 *   },
 *   onSessionExpired: () => {
 *     store.dispatch(setSessionExpired(true));
 *     store.dispatch(logoutUser());
 *   },
 * });
 *
 * const response = await authenticatedFetch('/api/endpoint', { method: 'GET' });
 * ```
 */
export const createAuthenticatedFetch = (config: HttpInterceptorConfig) => {
  const { getToken, refreshToken, onSessionExpired } = config;

  return async (url: string, options: RequestInit = {}): Promise<Response> => {
    // Build headers with auth token
    const buildHeaders = (token: string | null): HeadersInit => {
      const headers: Record<string, string> = {
        ...((options.headers as Record<string, string>) || {}),
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      return headers;
    };

    // Initial request with current token
    const token = getToken();
    let response = await fetch(url, {
      ...options,
      headers: buildHeaders(token),
    });

    // Handle 401 Unauthorized - attempt token refresh and retry
    if (response.status === 401) {
      try {
        // Attempt to refresh the token (single retry)
        const newToken = await refreshToken();

        if (newToken) {
          // Retry original request with new token
          response = await fetch(url, {
            ...options,
            headers: buildHeaders(newToken),
          });

          // If still 401 after refresh, session is truly expired
          if (response.status === 401) {
            onSessionExpired();
          }
        } else {
          // Token refresh returned null - session expired
          onSessionExpired();
        }
      } catch {
        // Token refresh threw an error - session expired
        onSessionExpired();
      }
    }

    return response;
  };
};

/**
 * Type for the authenticated fetch function
 */
export type AuthenticatedFetch = ReturnType<typeof createAuthenticatedFetch>;
